import { z } from 'zod';
import { EXECUTION_FAILURE_CLASSES, ScenarioExecutionSummaryResponseSchema } from './execution-intelligence';
import { FLAKE_MIN_RUNS_FOR_VISIBILITY, FLAKE_TIME_WINDOWS, FlakeTimeWindowSchema } from './flake-intelligence';
import { FAILURE_EXPLANATION_VERSION_STATUSES } from './run-failure-intelligence';

/**
 * QI-2 — Compose-on-read Scenario Execution Health / Reliability.
 *
 * Observational aggregate over quality_scenario_executions (+ linked signals).
 * Never Proof. Never mutates test_runs / execution status.
 * Never reuses scenario lifecycle health_status as reliability.
 *
 * Existing ScenarioExecutionSummaryResponse.health (ScenarioExecutionHealth)
 * remains the EI aggregate metrics object — this DTO is additive and named
 * `executionReliability` to avoid that collision.
 */

/** Minimum sample size before STABLE / FLAKY / FAILING / UNSTABLE may be stated. */
export const EXECUTION_RELIABILITY_MIN_SAMPLE = FLAKE_MIN_RUNS_FOR_VISIBILITY;

/**
 * Reliability vocabulary — NOT scenario lifecycle health_status
 * (healthy | needs_review | outdated | at_risk | archived).
 */
export const EXECUTION_RELIABILITY_STATES = [
  'NO_DATA',
  'UNKNOWN',
  'STABLE',
  'FLAKY',
  'UNSTABLE',
  'FAILING',
  'STALE',
] as const;
export type ExecutionReliabilityState = (typeof EXECUTION_RELIABILITY_STATES)[number];

export const EXECUTION_HEALTH_LATEST_RULE = 'latest_automation_terminal_outcome_by_completed_at' as const;

export const ExecutionHealthLatestSchema = z.object({
  executionId: z.string().uuid().nullable(),
  runId: z.string().uuid().nullable(),
  status: z.string().nullable(),
  completedAt: z.string().nullable(),
  scenarioVersion: z.number().int().positive().nullable(),
  versionStatus: z.enum(FAILURE_EXPLANATION_VERSION_STATUSES).nullable(),
  /** Explicit: this is the deterministic latest outcome, not reliability. */
  isLatestResult: z.literal(true),
});
export type ExecutionHealthLatest = z.infer<typeof ExecutionHealthLatestSchema>;

export const ExecutionHealthHistorySchema = z.object({
  window: FlakeTimeWindowSchema,
  windowDays: z.number().int().positive(),
  executionCount: z.number().int().nonnegative(),
  passedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  blockedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  cancelledCount: z.number().int().nonnegative(),
  retryCount: z.number().int().nonnegative(),
  healedCount: z.number().int().nonnegative(),
});
export type ExecutionHealthHistory = z.infer<typeof ExecutionHealthHistorySchema>;

export const ExecutionHealthReliabilitySchema = z.object({
  /** Qualified reliability state — never lifecycle health_status. */
  state: z.enum(EXECUTION_RELIABILITY_STATES),
  sampleSize: z.number().int().nonnegative(),
  sufficientSampleSize: z.boolean(),
  minSampleSize: z.literal(EXECUTION_RELIABILITY_MIN_SAMPLE),
  passRate: z.number().min(0).max(100).nullable(),
  failureRate: z.number().min(0).max(100).nullable(),
  /**
   * Statistical flake_rate in [0,1] using flakeRateFor semantics
   * (null when sample below FLAKE_MIN_RUNS_FOR_VISIBILITY).
   * Not Proof. Does not mutate execution status.
   */
  flakeRate: z.number().min(0).max(1).nullable(),
});
export type ExecutionHealthReliability = z.infer<typeof ExecutionHealthReliabilitySchema>;

export const ExecutionHealthFailureClassCountSchema = z.object({
  failureClass: z.enum(EXECUTION_FAILURE_CLASSES),
  count: z.number().int().nonnegative(),
});
export type ExecutionHealthFailureClassCount = z.infer<typeof ExecutionHealthFailureClassCountSchema>;

export const ExecutionHealthSignalsSchema = z.object({
  /** Dominant AUTOMATION_STABILITY among current-version window population (nullable). */
  stability: z.enum(['stable', 'healed', 'retried', 'unstable']).nullable(),
  healingRate: z.number().min(0).max(100).nullable(),
  retryRate: z.number().min(0).max(100).nullable(),
  /** Execution-level primary failure_class counts (not per cascading step). */
  failureClassCounts: z.array(ExecutionHealthFailureClassCountSchema),
  averageDurationMs: z.number().nonnegative().nullable(),
});
export type ExecutionHealthSignals = z.infer<typeof ExecutionHealthSignalsSchema>;

export const ExecutionHealthVersionSchema = z.object({
  currentScenarioVersion: z.number().int().positive().nullable(),
  staleExecutionCount: z.number().int().nonnegative(),
  /** True when stale (older scenario_version) executions were excluded from history/reliability. */
  staleExcluded: z.literal(true),
});
export type ExecutionHealthVersion = z.infer<typeof ExecutionHealthVersionSchema>;

export const ExecutionHealthGovernanceSchema = z.object({
  healthIsNotProof: z.literal(true),
  healthIsNotLatestResult: z.literal(true),
  aiIsAdvisory: z.literal(true),
  /** Lifecycle scenario.health_status must not be read as reliability. */
  reliabilityIsNotLifecycleHealthStatus: z.literal(true),
  /**
   * Existing flake populations include environment/network/browser failures
   * in failed counts — not silently corrected here.
   */
  environmentFailuresNotExcludedFromFlakeRate: z.literal(true),
  /** error_fingerprints remain org-scoped; not project-attributed here. */
  fingerprintsNotProjectAttributed: z.literal(true),
  fingerprintScope: z.literal('org'),
  latestExecutionRule: z.literal(EXECUTION_HEALTH_LATEST_RULE),
  noPersistence: z.literal(true),
});
export type ExecutionHealthGovernance = z.infer<typeof ExecutionHealthGovernanceSchema>;

/**
 * QI-2 compose-on-read payload. Named executionReliability so consumers
 * cannot confuse it with ScenarioExecutionHealth or lifecycle health_status.
 */
export const ScenarioExecutionReliabilitySchema = z.object({
  scenarioId: z.string().uuid(),
  flowId: z.string().uuid().nullable(),
  latest: ExecutionHealthLatestSchema,
  history: ExecutionHealthHistorySchema,
  reliability: ExecutionHealthReliabilitySchema,
  signals: ExecutionHealthSignalsSchema,
  version: ExecutionHealthVersionSchema,
  governance: ExecutionHealthGovernanceSchema,
  /** Supported flake windows (reuse flake-intelligence). */
  supportedWindows: z.array(z.enum(FLAKE_TIME_WINDOWS)),
});
export type ScenarioExecutionReliability = z.infer<typeof ScenarioExecutionReliabilitySchema>;

/**
 * Additive summary shape: existing EI summary + QI-2 executionReliability.
 * Prefer this over ScenarioExecutionSummaryResponse alone after QI-2.
 */
export const ScenarioExecutionSummaryWithReliabilitySchema = ScenarioExecutionSummaryResponseSchema.extend({
  executionReliability: ScenarioExecutionReliabilitySchema,
});
export type ScenarioExecutionSummaryWithReliability = z.infer<typeof ScenarioExecutionSummaryWithReliabilitySchema>;
